import { Subscription } from 'rxjs';
import { EwsRefresh } from '../../../../../services/ews-refresh';
import { OnDestroy, OnChanges, SimpleChanges, Component, ChangeDetectorRef, OnInit } from '@angular/core';
import { lastValueFrom, RmsMessageService, RmsPulldownService, WebService, InventoryDataService } from '@services';
import { RmsModule } from '@rms';
import { EwsDataService, EwsFilters } from '../../../../../services/ews-data.service';
import { GroupAnalysisComponent } from '../group-analysis/group-analysis.component';
import { GroupFilterComponent } from '../../group-filter/group-filter.component';

// =====================================================================
// GroupAnalysisPageComponent — Scope 6.4 "EWS Group Analysis" screen (C2.2)
// ---------------------------------------------------------------------
// Its own menu item under Inventory (traders open it directly, no Summary).
// Filter Type (same idea as SKU Pricing):
//   • Single Group  — legacy Country → Common Group (Next Export Month)
//   • Custom Group  — the trader's saved custom group filters, the SAME list
//                     as SKU Pricing (users/custom-group-filters, per user);
//                     "View Custom Filters" opens the shared create / edit
//                     pulldown (GroupFilterComponent)
//   • Multi-Group   — search + tick groups for a one-off analysis; can be
//                     kept with "Save as custom group"
// All groups of a run are analysed together (SKUs are de-duplicated by the
// proc). "View Report" shows the Group Analysis under the filter bar.
// Data: GET /api/v1/ews/options · users/custom-group-filters (+ /merge)
// =====================================================================

type FilterMode = 'Single' | 'Custom' | 'Multi';
const NO_COUNTRY = 0;      // groups without a country

interface GroupOption {
  GroupID: number;
  Label: string;           // "Brazil All Screens (SEP 2027)"
  Name: string;            // "Brazil All Screens" / "Brazil All Screens › Brazil screen 14/16"
  CountryKey: number;
  CountryName: string;
  Active: boolean;
  SkuCount: number;
  InExportWindow: boolean;
}

@Component({
  selector: 'app-group-analysis-page',
  imports: [RmsModule, GroupAnalysisComponent],
  templateUrl: './group-analysis-page.component.html',
  styleUrl: './group-analysis-page.component.scss'
})
export class GroupAnalysisPageComponent implements OnInit, OnDestroy {
  private refresh = new EwsRefresh();
  private liveSubscription?: Subscription;
  UpdatedAt: Date | null = null;
  ngOnDestroy() { this.refresh.destroy(); this.liveSubscription?.unsubscribe(); }

  LoadingOptions = false;

  // ---- filter type ----
  Mode: FilterMode = 'Single';
  ModeOptions = [
    { value: 'Single', label: 'Single Group' },
    { value: 'Custom', label: 'Custom Group Filter' },
    { value: 'Multi', label: 'Multi-Group' }
  ];

  // ---- filter bar (same defaults as the EWS Summary) ----
  Filters: EwsFilters = {
    BurnRange: 3,
    SafsValue: 10,
    SafsType: 'PCT',
    CropOption: 2,
    VolatileMarket: false,
    ExcludeFireSales: false,
    DaysInAdj: 0
  };
  private Defaults: EwsFilters = { ...this.Filters };

  BurnRangeOptions = [
    { value: 3, label: '3 months' },
    { value: 6, label: '6 months' },
    { value: 12, label: '12 months' }
  ];
  SafsTypeOptions = [
    { value: 'PCT', label: '% of burn' },
    { value: 'BAGS', label: 'Bags / month' }
  ];
  CropOptions = [
    { value: 1, label: 'Crop #1 only' },
    { value: 2, label: 'Crop #1 + #2' }
  ];

  // ---- single group: country → group ----
  AllGroups: GroupOption[] = [];
  private GroupById = new Map<number, GroupOption>();
  CountryOptions: { value: number; label: string }[] = [];
  SelectedCountry: number | null = null;
  GroupOptions: { value: number; label: string }[] = [];
  SelectedGroup: number | null = null;
  ShowInactive = false;

  // ---- custom group filters (shared with SKU Pricing, per user) ----
  CustomGroups: any[] = [];
  SelectedCustomGroupID: number | null = null;
  CustomGroupItems: { GroupID: number; Name: string; Missing: boolean }[] = [];
  SavingCustom = false;
  private RegionByCountry = new Map<number, number>();   // for "Save as custom group"

  // ---- multi-group mode ----
  MultiSearch = '';
  Picked: GroupOption[] = [];

  // ---- report ----
  RunKeys: number[] = [];          // one key per "View Report" → re-creates the analysis
  RunGroupIDs: number[] = [];
  RunFilters: EwsFilters = {};
  RunTitle = '';
  private runCounter = 0;

  constructor(
    private ews: EwsDataService,
    private msg: RmsMessageService,
    private web: WebService,
    private pulldownService: RmsPulldownService,
    private inventoryData: InventoryDataService,
    private cdr: ChangeDetectorRef
  ) { }

  async ngOnInit() {
    await Promise.all([this.loadOptions(), this.loadCustomGroups(), this.loadRegions()]);
  }

  get IsMulti(): boolean { return this.Mode === 'Multi'; }
  get IsCustom(): boolean { return this.Mode === 'Custom'; }

  /** group ids the current filter type would analyse */
  private get SelectedGroupIDs(): number[] {
    if (this.Mode === 'Multi') return this.Picked.map(p => p.GroupID);
    if (this.Mode === 'Custom') return this.CustomGroupItems.filter(i => !i.Missing).map(i => i.GroupID);
    return this.SelectedGroup ? [this.SelectedGroup] : [];
  }

  get CanRun(): boolean {
    return this.SelectedGroupIDs.length > 0;
  }

  queueRefresh() {
    this.refresh.schedule(() => {
      if (this.CanRun) this.viewReport();
      else { this.RunKeys = []; this.LastRunKey = null; this.cdr.markForCheck(); }
    });
  }

  onModeChange(mode: FilterMode) {
    this.Mode = mode;
    this.queueRefresh();
    // a trader's "Primary" custom group is picked for them (same flag as SKU Pricing)
    if (mode === 'Custom' && !this.SelectedCustomGroupID) {
      const primary = this.CustomGroups.find(g => g.PrimaryFilter === true || g.PrimaryFilter === 1);
      if (primary) this.onCustomGroupChange(primary.CustomGroupFilterID);
    }
  }

  // ---------------------------------------------------------------------
  // Options
  // ---------------------------------------------------------------------

  async loadOptions() {
    this.LoadingOptions = true;
    try {
      const opt = await lastValueFrom(this.ews.getOptions());

      // saved defaults (same settings the Summary uses)
      const setting = (key: string) => (opt?.Settings ?? []).find((s: any) => s.SettingKey === key)?.SettingValue;
      const burn = Number(setting('DefaultBurnRange'));
      const safs = Number(setting('DefaultSafsValue'));
      const safsType = setting('DefaultSafsType');
      const crop = Number(setting('DefaultCropOption'));
      if ([3, 6, 12].includes(burn)) this.Filters.BurnRange = burn as any;
      if (setting('DefaultSafsValue') != null && !Number.isNaN(safs)) this.Filters.SafsValue = safs;
      if (safsType === 'PCT' || safsType === 'BAGS') this.Filters.SafsType = safsType;
      if (crop === 1 || crop === 2) this.Filters.CropOption = crop;
      this.Defaults = { ...this.Filters };

      // groups with something to analyse (SKUs in the group or its children)
      this.AllGroups = (opt?.Groups ?? [])
        .filter((g: any) => (g.TotalSkuCount ?? g.OwnSkuCount ?? 0) > 0)
        .map((g: any) => {
          const name = g.ParentGroupName ? `${g.ParentGroupName} › ${g.GroupName}` : g.GroupName;
          const next = exportLabel(g.NextExportMonth);
          return {
            GroupID: g.GroupID,
            Name: name,
            Label: next ? `${name} (${next})` : name,
            CountryKey: g.CountryID ?? NO_COUNTRY,
            CountryName: g.CountryName || (g.CountryID ? `Country #${g.CountryID}` : 'No country'),
            Active: g.Active !== false && g.Active !== 0,
            SkuCount: g.TotalSkuCount ?? g.OwnSkuCount ?? 0,
            InExportWindow: !!g.InExportWindow
          } as GroupOption;
        })
        .sort((a: GroupOption, b: GroupOption) => a.Name.localeCompare(b.Name));
      this.GroupById = new Map(this.AllGroups.map(g => [g.GroupID, g]));

      this.buildCountries();
      this.buildCustomItems();       // custom groups may have loaded first
    } catch (err: any) {
      this.msg.error(err?.error?.error ?? 'Could not load the coffee groups.');
    } finally {
      this.LoadingOptions = false;
      this.cdr.markForCheck();
    }
  }

  private visibleGroups(): GroupOption[] {
    return this.AllGroups.filter(g => this.ShowInactive || g.Active);
  }

  private buildCountries() {
    const map = new Map<number, { name: string; count: number }>();
    for (const g of this.visibleGroups()) {
      const c = map.get(g.CountryKey) ?? { name: g.CountryName, count: 0 };
      c.count++;
      map.set(g.CountryKey, c);
    }
    this.CountryOptions = [...map.entries()]
      .map(([value, c]) => ({ value, label: `${c.name} (${c.count})` }))
      .sort((a, b) => (a.value === NO_COUNTRY ? 1 : b.value === NO_COUNTRY ? -1 : a.label.localeCompare(b.label)));
    this.buildGroupOptions();
  }

  private buildGroupOptions() {
    this.GroupOptions = this.visibleGroups()
      .filter(g => g.CountryKey === this.SelectedCountry)
      .map(g => ({ value: g.GroupID, label: g.Label }));
    if (!this.GroupOptions.some(o => o.value === this.SelectedGroup)) this.SelectedGroup = null;
  }

  onCountryChange(value: any) {
    this.SelectedCountry = value === null || value === undefined || value === '' ? null : Number(value);
    this.buildGroupOptions();
  }

  onShowInactiveChange(value: boolean) {
    this.ShowInactive = !!value;
    this.buildCountries();
  }

  // ---------------------------------------------------------------------
  // Custom group filters (same API + pulldown as SKU Pricing)
  // ---------------------------------------------------------------------

  async loadCustomGroups() {
    try {
      const rows: any[] = await lastValueFrom(this.web.getWebCall('users', 'custom-group-filters', {
        params: [{ name: 'UserID', value: localStorage.getItem('GUID') || '' }]
      }));
      this.CustomGroups = (rows || []).slice().sort((a: any, b: any) =>
        String(a.CustomGroupFilterName ?? '').localeCompare(String(b.CustomGroupFilterName ?? '')));
      if (this.SelectedCustomGroupID && !this.CustomGroups.some(g => Number(g.CustomGroupFilterID) === Number(this.SelectedCustomGroupID))) {
        this.SelectedCustomGroupID = null;      // deleted / renamed elsewhere
      }
      this.buildCustomItems();
    } catch {
      this.msg.error('Could not load your custom group filters.');
    } finally {
      this.cdr.markForCheck();
    }
  }

  /** region per country, so a Multi-Group pick can be saved in the shared format */
  private async loadRegions() {
    try {
      const data = await this.inventoryData.getData({ keys: ['Countries'] });
      for (const c of data?.['Countries'] ?? []) {
        const region = Number(c.RegionID ?? c.RegionId);
        if (Number.isFinite(region)) this.RegionByCountry.set(Number(c.CountryID), region);
      }
    } catch {
      // only needed for "Save as custom group"
    }
  }

  onCustomGroupChange(value: any) {
    this.SelectedCustomGroupID = value === null || value === undefined || value === '' ? null : Number(value);
    this.buildCustomItems();
  }

  /** the groups inside the selected custom filter (JSON [{RegionID, CountryID, GroupID}]) */
  private buildCustomItems() {
    const cg = this.CustomGroups.find(g => Number(g.CustomGroupFilterID) === Number(this.SelectedCustomGroupID));
    if (!cg) { this.CustomGroupItems = []; return; }

    let rows: any[] = [];
    try { rows = JSON.parse(cg.CustomGroupFilter || '[]'); } catch { rows = []; }
    if (!Array.isArray(rows)) rows = [];

    const ids = [...new Set(rows
      .map((r: any) => Number(r.GroupID ?? r.CoffeeSKUGroupID))
      .filter((x: number) => Number.isFinite(x) && x > 0))];

    this.CustomGroupItems = ids.map(id => {
      const g = this.GroupById.get(id);
      return { GroupID: id, Name: g ? g.Label : `Group #${id}`, Missing: !g };
    });
  }

  get SelectedCustomGroup(): any {
    return this.CustomGroups.find(g => Number(g.CustomGroupFilterID) === Number(this.SelectedCustomGroupID)) ?? null;
  }

  get MissingCustomCount(): number {
    return this.CustomGroupItems.filter(i => i.Missing).length;
  }

  /** create / edit / set primary — the shared pulldown from SKU Pricing */
  viewCustomFilters() {
    const ref = this.pulldownService.open(GroupFilterComponent, {
      data: { customGroups: this.CustomGroups || [] },
      title: 'Custom Group Filters',
      width: '96vw',
      top: '0',
      maxHeight: '100vh',
      minHeight: '30vh'
    });

    ref.returnData().subscribe(async (data: any) => {
      if (data?.action === 'refresh') {
        await this.loadCustomGroups();
        ref.updateData({ customGroups: this.CustomGroups });
      }
    });

    ref.afterClosed().subscribe(async () => {
      await this.loadCustomGroups();
    });
  }

  /** keep a Multi-Group pick as a custom group filter (shows in SKU Pricing too) */
  async saveAsCustomGroup() {
    if (!this.Picked.length || this.SavingCustom) return;
    const name = (prompt('Name for this custom group filter:', '') ?? '').trim();
    if (!name) return;
    if (this.CustomGroups.some(g => String(g.CustomGroupFilterName ?? '').trim().toLowerCase() === name.toLowerCase())) {
      this.msg.warn(`You already have a custom group called "${name}". Pick another name, or edit it under View Custom Filters.`);
      return;
    }

    const payload = {
      CustomGroupFilterID: null,
      UserGUID: localStorage.getItem('GUID') || '',
      CustomGroupFilterName: name,
      CustomGroupFilter: JSON.stringify(this.Picked.map(p => ({
        RegionID: p.CountryKey ? (this.RegionByCountry.get(p.CountryKey) ?? null) : null,
        CountryID: p.CountryKey || null,
        GroupID: p.GroupID
      }))),
      PrimaryFilter: this.CustomGroups.length === 0          // first one becomes primary (same rule as SKU Pricing)
    };

    this.SavingCustom = true;
    try {
      await lastValueFrom(this.web.getWebCall('users', 'custom-group-filters/merge', { body: payload }));
      await this.loadCustomGroups();
      const saved = this.CustomGroups.find(g => g.CustomGroupFilterName === name);
      this.msg.success(`Custom group "${name}" saved — it's in Custom Group Filter here and in SKU Pricing.`);
      if (saved) {
        this.Mode = 'Custom';
        this.onCustomGroupChange(saved.CustomGroupFilterID);
      }
    } catch {
      this.msg.error('Could not save the custom group.');
    } finally {
      this.SavingCustom = false;
      this.cdr.markForCheck();
    }
  }

  // ---------------------------------------------------------------------
  // Multi-group mode
  // ---------------------------------------------------------------------

  /** groups matching the search, minus the ones already picked (max 60 shown) */
  get MultiMatches(): GroupOption[] {
    const q = this.MultiSearch.trim().toLowerCase();
    const picked = new Set(this.Picked.map(p => p.GroupID));
    return this.visibleGroups()
      .filter(g => !picked.has(g.GroupID))
      .filter(g => !q || g.Label.toLowerCase().includes(q) || g.CountryName.toLowerCase().includes(q))
      .slice(0, 60);
  }

  /** how many groups match the search, picked ones included (for the "nothing left" message) */
  get SearchHitCount(): number {
    const q = this.MultiSearch.trim().toLowerCase();
    return this.visibleGroups()
      .filter(g => !q || g.Label.toLowerCase().includes(q) || g.CountryName.toLowerCase().includes(q)).length;
  }

  pick(g: GroupOption) {
    if (!this.Picked.some(p => p.GroupID === g.GroupID)) this.Picked = [...this.Picked, g];
    this.queueRefresh();
  }

  unpick(g: GroupOption) {
    this.Picked = this.Picked.filter(p => p.GroupID !== g.GroupID);
    this.queueRefresh();
  }

  /** tick every match of the current search at once (e.g. search "brazil") */
  pickAllMatches() {
    this.Picked = [...this.Picked, ...this.MultiMatches];
    this.queueRefresh();
  }

  // ---------------------------------------------------------------------
  // Run / clear
  // ---------------------------------------------------------------------

  /** what the report on screen was made with (null = no report yet) */
  private LastRunKey: string | null = null;

  private filterKey(): string {
    const f = this.Filters;
    return JSON.stringify([[...this.SelectedGroupIDs].sort((a, b) => a - b), f.BurnRange, String(f.SafsValue ?? ''), f.SafsType,
    f.CropOption, String(f.DaysInAdj ?? ''), !!f.VolatileMarket, !!f.ExcludeFireSales]);
  }

  /** groups or filters changed after the last View report */
  get FiltersChanged(): boolean {
    return this.LastRunKey !== null && this.RunKeys.length > 0 && this.filterKey() !== this.LastRunKey;
  }

  viewReport() {
    const ids = this.SelectedGroupIDs;
    if (!ids.length) {
      this.msg.warn(this.Mode === 'Multi' ? 'Tick at least one coffee group.'
        : this.Mode === 'Custom' ? 'Pick a custom group filter that has groups in it.'
          : 'Pick a coffee group.');
      return;
    }
    this.RunGroupIDs = ids;
    this.RunTitle = this.Mode === 'Custom' && this.SelectedCustomGroup
      ? `Custom group: ${this.SelectedCustomGroup.CustomGroupFilterName}` : '';
    this.RunFilters = { ...this.Filters, WarehouseID: null, GroupIDs: null, IncludeInactive: false };
    this.RunKeys = [1];       // new key → Angular builds a fresh analysis
    this.LastRunKey = this.filterKey();
    this.cdr.markForCheck();
  }

  clearAll() {
    this.refresh.begin();
    this.Filters = { ...this.Defaults };
    this.SelectedCountry = null;
    this.SelectedGroup = null;
    this.SelectedCustomGroupID = null;
    this.CustomGroupItems = [];
    this.Picked = [];
    this.MultiSearch = '';
    this.RunKeys = [];
    this.LastRunKey = null;
    this.buildGroupOptions();
  }
}

/** '2027-09-01…' → "SEP 2027" (legacy label) */
function exportLabel(v: any): string {
  if (!v) return '';
  const d = String(v).substring(0, 10);
  const names = ['JAN', 'FEB', 'MAR', 'APR', 'MAY', 'JUN', 'JUL', 'AUG', 'SEP', 'OCT', 'NOV', 'DEC'];
  return `${names[+d.substring(5, 7) - 1]} ${d.substring(0, 4)}`;
}
